import {
  conspiracyOrganizedRecords,
  corporateRecords,
  criminalRecordsMenu,
  destructionRecords,
  drugRecords,
  harassmentRecords,
  otherRecords,
  sexOffenseRecords,
  societalRecords,
  theftOffenseRecords,
  violentRecords
} from "shared/app/VerificationTemplate/slices/DecisionCriteria/slices/RuleCriteria/constants";
import {
  FieldName,
  Rule
} from "shared/app/VerificationTemplate/slices/DecisionCriteria/slices/RuleCriteria/types";
import {
  offenseSelectionLabel,
  offenseSelectionNames
} from "shared/app/VerificationTemplate/slices/DecisionCriteria/slices/RuleCriteria/criminalOffenseSelection";
import { formatTitleFirstWord } from "utils/formatTitle";
import { readableFieldName, readableFieldSubtitle } from "./constants";

const fieldsWithSubcategory = [
  FieldName.FelonyRecords,
  FieldName.MisdemeanorRecords,
  FieldName.OtherCriminalRecords,
  FieldName.AllCriminalRecords
];

export const formatThreshold = (rule: Rule) => {
  if (
    [FieldName.SexOffenderCheck, FieldName.IncomeDocumentFraudCheck].includes(
      rule.field_name
    )
  )
    return rule.threshold === 1 || rule.threshold === "1" ? "Yes" : "No";
  else {
    if (rule.threshold === "true") return "Yes";
    if (rule.threshold === "false") return "No";
    return rule.threshold || "-";
  }
};

export const formatConditionTitle = (
  field_name?: FieldName,
  subCategories?: string[]
) => {
  if (!field_name) return "-";

  if (field_name === FieldName.OfacRecords)
    return "Sanctions (OFAC) convictions";

  const hasSubcategory = fieldsWithSubcategory.find((key) =>
    field_name.includes(key)
  );

  if (hasSubcategory) {
    const subcategories = {
      ...drugRecords(hasSubcategory),
      ...violentRecords(hasSubcategory),
      ...sexOffenseRecords(hasSubcategory),
      ...theftOffenseRecords(hasSubcategory),
      ...destructionRecords(hasSubcategory),
      ...harassmentRecords(hasSubcategory),
      ...societalRecords(hasSubcategory),
      ...conspiracyOrganizedRecords(hasSubcategory),
      ...corporateRecords(hasSubcategory),
      ...otherRecords(hasSubcategory)
    };

    const menuItems = criminalRecordsMenu(true)[hasSubcategory];
    const currentSubcategory =
      offenseSelectionNames(menuItems, field_name, subCategories) ||
      offenseSelectionLabel(menuItems, field_name, subCategories) ||
      subcategories[field_name] ||
      "";

    return (
      `${formatTitleFirstWord(hasSubcategory).replaceAll("records", "convictions")}` +
      "   " +
      `${currentSubcategory.toLowerCase()}`
    );
  }

  return (
    readableFieldName[field_name as string] ||
    formatTitleFirstWord(field_name).replaceAll("records", "convictions")
  );
};

export const formatConditionSubTitle = (field_name: FieldName) => {
  return readableFieldSubtitle[field_name];
};

export const formatApplicantValue = (rule: Rule, value: string | number) => {
  if (
    [FieldName.SexOffenderCheck, FieldName.IncomeDocumentFraudCheck].includes(
      rule.field_name
    )
  )
    return value === 1 || value === "1" ? "Yes" : "No";
  else return String(value);
};

const parseNumericRuleValue = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const formatNumberValue = (value: number) => {
  const rounded = Math.round(value * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
};

export const formatDisplayedComputedValue = (
  computedValue: number | undefined,
  threshold: string | number | undefined
) => {
  const parsedComputed = parseNumericRuleValue(computedValue);
  if (parsedComputed !== null) return formatNumberValue(parsedComputed);

  const parsedThreshold = parseNumericRuleValue(threshold);
  if (parsedThreshold !== null) return "0";

  return "-";
};
